import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useArrowsAiAccount } from 'dashboard/composables/useArrowsAiAccount';
import ArrowsAiAssistantsAPI from 'dashboard/api/arrowsAi/assistants';
import {
  fromApi,
  toApiPayload,
} from 'dashboard/composables/arrowsAi/assistantMapper';
import { throwErrorMessage } from 'dashboard/store/utils/api';

const LAST_ASSISTANT_PREFIX = 'arrows_ai_last_assistant';
const assistants = ref([]);
const isLoading = ref(false);
const rememberedAssistantId = ref(null);
let loadedAccountId = null;

const lineDiff = (previous = '', next = '') => {
  const before = previous.split('\n');
  const after = next.split('\n');

  return {
    added: after.filter(line => line && !before.includes(line)),
    removed: before.filter(line => line && !after.includes(line)),
  };
};

const buildHistoryEntry = (assistant, nextInstructions, user) => {
  if (assistant.instructions === nextInstructions) return null;

  const { added, removed } = lineDiff(assistant.instructions, nextInstructions);

  return {
    id: Date.now(),
    createdAt: new Date().toISOString(),
    userName: user?.name || user?.available_name || 'Unknown',
    content: nextInstructions,
    added,
    removed,
    reordered: !added.length && !removed.length,
  };
};

const replaceAssistant = updated => {
  assistants.value = assistants.value.map(item =>
    item.id === updated.id ? updated : item
  );
};

export function useArrowsAiAssistants() {
  const route = useRoute();
  const { accountId } = useAccount();
  const { isAdmin } = useAdmin();
  const currentUser = useMapGetter('getCurrentUser');
  const { assistantLimit, inboxesPerAssistant } = useArrowsAiAccount();
  const canAssignInbox = computed(() => assistantLimit.value > 0);
  const createLimit = computed(() =>
    assistantLimit.value === 0 ? 1 : assistantLimit.value
  );

  const lastAssistantKey = computed(
    () => `${LAST_ASSISTANT_PREFIX}:${accountId.value}`
  );

  const rememberAssistant = id => {
    if (!accountId.value || !id) return;
    rememberedAssistantId.value = Number(id);
    localStorage.setItem(lastAssistantKey.value, String(id));
  };

  const getAssistant = id =>
    assistants.value.find(item => item.id === Number(id)) || null;

  const fetchAssistants = async id => {
    if (!isAdmin.value) {
      assistants.value = [];
      loadedAccountId = null;
      return;
    }
    if (!id || loadedAccountId === id) return;

    isLoading.value = true;
    try {
      const { data } = await ArrowsAiAssistantsAPI.get();
      assistants.value = (Array.isArray(data) ? data : []).map(fromApi);
      loadedAccountId = id;
    } catch {
      assistants.value = [];
      loadedAccountId = id;
    } finally {
      isLoading.value = false;
    }
  };

  const refreshAssistants = () => {
    loadedAccountId = null;
    return fetchAssistants(accountId.value);
  };

  watch([accountId, isAdmin], ([id]) => fetchAssistants(id), {
    immediate: true,
  });

  watch(accountId, () => {
    rememberedAssistantId.value = null;
  });

  const canCreateAssistant = computed(
    () => assistants.value.length < createLimit.value
  );

  const createAssistant = async payload => {
    if (!canCreateAssistant.value) return null;

    try {
      const { data } = await ArrowsAiAssistantsAPI.create({
        assistant: toApiPayload({
          name: payload.name.trim(),
          description: payload.description.trim(),
          features: payload.features,
        }),
      });
      const assistant = fromApi(data);
      assistants.value = [assistant, ...assistants.value];
      rememberAssistant(assistant.id);
      return assistant;
    } catch (error) {
      return throwErrorMessage(error);
    }
  };

  const updateAssistant = async (id, payload) => {
    const current = getAssistant(id);
    if (!current) return null;

    const nextInstructions = payload.instructions ?? current.instructions;
    const historyEntry = buildHistoryEntry(
      current,
      nextInstructions,
      currentUser.value
    );
    const nextPayload = {
      ...payload,
      promptHistory: historyEntry
        ? [historyEntry, ...current.promptHistory].slice(0, 30)
        : payload.promptHistory,
    };

    try {
      const { data } = await ArrowsAiAssistantsAPI.update(id, {
        assistant: toApiPayload(nextPayload),
      });
      const updated = fromApi(data);
      replaceAssistant(updated);
      rememberAssistant(updated.id);
      return updated;
    } catch (error) {
      return throwErrorMessage(error);
    }
  };

  const deleteAssistant = async id => {
    const numericId = Number(id);
    try {
      await ArrowsAiAssistantsAPI.delete(numericId);
    } catch (error) {
      return throwErrorMessage(error);
    }
    assistants.value = assistants.value.filter(item => item.id !== numericId);
    [
      'arrows_ai_faqs',
      'arrows_ai_documents',
      'arrows_ai_services',
      'arrows_ai_tools',
    ].forEach(prefix => {
      localStorage.removeItem(`${prefix}:${accountId.value}:${numericId}`);
    });
    const nextId = assistants.value[0]?.id;
    if (nextId) rememberAssistant(nextId);
    else localStorage.removeItem(lastAssistantKey.value);
    rememberedAssistantId.value = nextId ? Number(nextId) : null;
    return nextId || null;
  };

  const connectInbox = async (id, inboxId) => {
    const current = getAssistant(id);
    if (!current) return null;
    if (!canAssignInbox.value) return null;
    if ((current.inboxIds || []).length >= inboxesPerAssistant.value) {
      return null;
    }

    try {
      await ArrowsAiAssistantsAPI.connectInbox(id, inboxId);
    } catch (error) {
      return throwErrorMessage(error);
    }

    const updated = { ...current, inboxIds: [...current.inboxIds, inboxId] };
    replaceAssistant(updated);
    return updated;
  };

  const disconnectInbox = async (id, inboxId) => {
    const current = getAssistant(id);
    if (!current) return null;

    try {
      await ArrowsAiAssistantsAPI.disconnectInbox(id, inboxId);
    } catch (error) {
      return throwErrorMessage(error);
    }

    const updated = {
      ...current,
      inboxIds: current.inboxIds.filter(item => item !== inboxId),
    };
    replaceAssistant(updated);
    return updated;
  };

  return {
    assistants: computed(() => assistants.value),
    isLoading: computed(() => isLoading.value),
    activeAssistantId: computed(() => {
      const routeId = Number(route.params.assistantId);
      if (getAssistant(routeId)) return routeId;

      const stored =
        rememberedAssistantId.value ||
        Number(localStorage.getItem(lastAssistantKey.value));
      if (getAssistant(stored)) return stored;
      return assistants.value[0]?.id || null;
    }),
    assistantLimit,
    createLimit,
    canAssignInbox,
    canCreateAssistant,
    getAssistant,
    rememberAssistant,
    refreshAssistants,
    createAssistant,
    updateAssistant,
    deleteAssistant,
    connectInbox,
    disconnectInbox,
  };
}
